import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  useTheme,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import {
  useGetAssociatedCampaignQuery,
  useUpdateAssociatedCampaignMutation,
  useGetPlatformMappingsQuery,
  PlatformMapping,
  useGetPlatformCandidatesMutation,
  useGetBriefingByIdQuery,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import { CandidateSelector } from "../../components/briefings/CandidateSelector";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import OrbLoader from "../../components/common/OrbLoader";
import { useGoBack } from "../../hooks/useGoBack";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type EntityType = components["schemas"]["EntityType"];

interface FormState {
  name: string;
  sourceId: string;
  platform: BriefingPlatform;
  campaignLevel: EntityType;
  advertiserId: string;
  briefingCampaignType?: components["schemas"]["BriefingCampaignType"] | null;
  campaignFormat?: components["schemas"]["CampaignFormats"] | null;
  nativeAdProvider?: "TABOOLA" | "OUTBRAIN" | null;
  investmentPayment?: "CLIENTE" | "TI_MEDIA_GROUP_SL" | null;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const defaultState: FormState = {
  name: "",
  sourceId: "",
  platform: "GOOGLE_ADS_SEM",
  campaignLevel: "CAMPAIGN",
  advertiserId: "",

  briefingCampaignType: null,
  campaignFormat: "ALL",
  nativeAdProvider: null,
  investmentPayment: null,
};

const EditAssociatedCampaign: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const { id: briefingId, associatedId } = useParams<{
    id: string;
    associatedId: string;
  }>();

  const canManageBriefings =
    userDb?.role === "ADMIN" ||
    userDb?.role === "ACCOUNT" ||
    userDb?.role === "TECHNICIAN";

  const {
    data: associatedCampaignResponse,
    isFetching: isFetchingCampaign,
    error: campaignError,
  } = useGetAssociatedCampaignQuery(
    {
      briefingId: briefingId ?? "",
      associatedId: associatedId ?? "",
    },
    { skip: !canManageBriefings || !briefingId || !associatedId }
  );

  const { data: platformMappingsData, isLoading: isLoadingMappings } =
    useGetPlatformMappingsQuery();
  const platformMappings: PlatformMapping[] = useMemo(
    () => platformMappingsData?.data ?? [],
    [platformMappingsData]
  );

  const { data: briefingResponse } = useGetBriefingByIdQuery(
    { id: briefingId ?? "" },
    { skip: !briefingId }
  );

  const isReadyForProduction =
    briefingResponse?.data?.status === "READY_FOR_PRODUCTION";
  const hasPermission =
    userDb?.role === "ADMIN" ||
    userDb?.role === "ACCOUNT" ||
    (userDb?.role === "TECHNICIAN" && isReadyForProduction);

  const [updateAssociatedCampaign, { isLoading: isUpdating }] =
    useUpdateAssociatedCampaignMutation();

  const [formState, setFormState] = useState<FormState>(defaultState);
  const [errors, setErrors] = useState<FormErrors>({});

  const platformEntry = useMemo(
    () => platformMappings.find((m) => m.platform === formState.platform),
    [platformMappings, formState.platform]
  );
  const defaultCampaignLevel =
    platformEntry?.defaultCampaignLevel ?? "CAMPAIGN";
  const canFetchCampaigns =
    !!platformEntry && !!briefingResponse?.data?.companyId;

  const [
    getPlatformCandidates,
    {
      data: platformCandidatesData,
      isLoading: isLoadingCandidates,
      error: candidatesError,
    },
  ] = useGetPlatformCandidatesMutation();

  useEffect(() => {
    if (formState.platform && briefingId && briefingResponse?.data?.companyId) {
      getPlatformCandidates({
        id: briefingId,
        body: { platformType: formState.platform },
      });
    }
  }, [
    formState.platform,
    briefingId,
    briefingResponse?.data?.companyId,
    getPlatformCandidates,
  ]);

  const currentMapping = useMemo(
    () => platformMappings.find((m) => m.platform === formState.platform),
    [platformMappings, formState.platform]
  );

  useEffect(() => {
    if (associatedCampaignResponse?.data) {
      const campaign = associatedCampaignResponse.data;
      setFormState({
        name: campaign.name,
        sourceId: campaign.sourceId ?? "",
        platform: campaign.platform,
        campaignLevel: campaign.campaignLevel,
        advertiserId: campaign.advertiserId ?? "",
        briefingCampaignType: campaign.briefingCampaignType,
        campaignFormat: campaign.campaignFormat ?? "ALL",
        nativeAdProvider: campaign.nativeAdProvider ?? null,
        investmentPayment: campaign.investmentPayment ?? null,
      });
    }
  }, [associatedCampaignResponse]);

  // Misma fuente que el dialogo de creacion: el mapping del backend. Antes era una
  // lista hardcodeada aparte, que se desincronizaba con cada plataforma nueva.
  const platformOptions = useMemo<BriefingPlatform[]>(
    () => platformMappings.map((m) => m.platform as BriefingPlatform),
    [platformMappings]
  );

  const levelOptions = useMemo<EntityType[]>(
    () => ["CAMPAIGN", "INSERTIONORDER", "LINEITEM"],
    []
  );

  // Los types tambien vienen del mapping, no de un switch por plataforma: ese
  // switch ya se habia quedado sin SHORTS, MASTHEAD ni PAUSEAD en YouTube.
  const getCampaignTypesForPlatform = (
    platform: BriefingPlatform
  ): components["schemas"]["BriefingCampaignType"][] =>
    platformMappings.find((m) => m.platform === platform)?.campaignTypes ?? [];

  if (!canManageBriefings) {
    return <Navigate to="/" replace />;
  }

  const handleBack = () => {
    if (briefingId) {
      goBack(`/briefings/${briefingId}`);
    } else {
      goBack("/briefings");
    }
  };

  const handleChange = (
    field: keyof FormState,
    value: string | number | boolean | null
  ) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "platform") {
        next.sourceId = "";
        next.briefingCampaignType = null;
        next.nativeAdProvider = null;
        next.campaignLevel =
          platformMappings.find(
            (m) => m.platform === (value as BriefingPlatform)
          )?.defaultCampaignLevel ?? "CAMPAIGN";
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formState.name.trim()) newErrors.name = "Name is required";
    if (formState.platform === "NATADS" && !formState.nativeAdProvider) {
      newErrors.nativeAdProvider =
        "Select a Native Ad Provider (TABOOLA or OUTBRAIN).";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!briefingId || !associatedId || !validate()) {
      return;
    }

    try {
      const payload: components["schemas"]["AssociatedCampaignRequestBody"] = {
        name: formState.name,
        sourceId: formState.sourceId || null,
        platform: formState.platform,
        campaignLevel: formState.campaignLevel,
        advertiserId:
          formState.campaignLevel === "INSERTIONORDER"
            ? formState.advertiserId.trim() || null
            : null,
        briefingCampaignType: formState.briefingCampaignType ?? undefined,
        campaignFormat: formState.campaignFormat ?? undefined,
        nativeAdProvider:
          formState.platform === "NATADS"
            ? (formState.nativeAdProvider ?? undefined)
            : undefined,
        investmentPayment: formState.investmentPayment ?? undefined,
      };

      await updateAssociatedCampaign({
        briefingId,
        associatedId,
        body: payload,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t(
            "briefingsEdit.associatedUpdateSuccess",
            "Associated campaign updated"
          ),
          severity: "success",
        })
      );
      navigate(`/briefings/${briefingId}`);
    } catch (error: unknown) {
      console.error("Update associated campaign failed", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsEdit.associatedUpdateError",
            "Failed to update associated campaign"
          ),
          severity: "error",
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Button
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              sx={{ alignSelf: "flex-start" }}
              onClick={handleBack}
            >
              {t("briefingsCreate.back")}
            </Button>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {t("briefingsEdit.associatedTitle", "Edit associated campaign")}
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[600]}>
              {t(
                "briefingsEdit.associatedSubtitle",
                "Update associated campaign details"
              )}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          {(!hasPermission ||
            briefingResponse?.data?.status === "FINALIZED") && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                zIndex: 2,
              }}
            >
              <Alert severity="error">
                {t(
                  "briefingsView.editNotAllowed",
                  "You cannot edit this associated campaign."
                )}
              </Alert>
            </Box>
          )}

          {isFetchingCampaign && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                zIndex: 1,
              }}
            >
              <OrbLoader size="medium" />
            </Box>
          )}

          {campaignError && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error">
                {t(
                  "briefingsView.associatedCampaignError",
                  "Unable to load associated campaign."
                )}
              </Alert>
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("briefingsCreate.form.name")}
                  value={formState.name}
                  onChange={(event) => handleChange("name", event.target.value)}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CandidateSelector
                  selectedPlatform={formState.platform}
                  defaultCampaignLevel={defaultCampaignLevel}
                  sourceId={formState.sourceId}
                  setSourceId={(id) => handleChange("sourceId", id)}
                  setCampaignLevel={(level) =>
                    handleChange("campaignLevel", level)
                  }
                  setAdvertiserId={(id) => handleChange("advertiserId", id)}
                  setNativeAdProvider={(provider) =>
                    handleChange("nativeAdProvider", provider)
                  }
                  canFetchCampaigns={canFetchCampaigns}
                  isBusy={isLoadingCandidates}
                  candidatesError={candidatesError}
                  candidatesData={platformCandidatesData?.data}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="campaign-platform">
                      {t("briefingsView.platform", "Platform")}
                    </InputLabel>
                    <Select
                      labelId="campaign-platform"
                      label={t("briefingsView.platform", "Platform")}
                      value={formState.platform}
                      onChange={(event) =>
                        handleChange(
                          "platform",
                          event.target.value as BriefingPlatform
                        )
                      }
                      disabled={isLoadingMappings}
                    >
                      {platformOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {currentMapping && (
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t(
                              "platformMappings.mappingDetails",
                              "Mapping Details"
                            )}
                          </Typography>
                          <Typography variant="body2">
                            <strong>
                              {t(
                                "platformMappings.realPlatform",
                                "Real Platform"
                              )}
                              :
                            </strong>{" "}
                            {currentMapping.realPlatform || "-"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>
                              {t("platformMappings.campaignTypes", "Types")}:
                            </strong>{" "}
                            {currentMapping.campaignTypes.join(", ")}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="right"
                    >
                      <IconButton
                        size="small"
                        color="info"
                        sx={{ flexShrink: 0 }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="campaign-level">
                    {t("briefingsView.level", "Level")}
                  </InputLabel>
                  <Select
                    labelId="campaign-level"
                    label={t("briefingsView.level", "Level")}
                    value={formState.campaignLevel}
                    onChange={(event) =>
                      handleChange(
                        "campaignLevel",
                        event.target.value as EntityType
                      )
                    }
                  >
                    {levelOptions.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {formState.campaignLevel === "INSERTIONORDER" && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("briefingsView.advertiserId", "Advertiser ID")}
                    value={formState.advertiserId}
                    onChange={(event) =>
                      handleChange("advertiserId", event.target.value)
                    }
                    fullWidth
                    helperText={t(
                      "briefingsView.advertiserIdHelp",
                      "Optional. Defaults to the company's first DV360 advertiser ID."
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="campaign-format-label">
                    {t("briefingsView.campaignFormat", "Campaign Format")}
                  </InputLabel>
                  <Select
                    labelId="campaign-format-label"
                    label={t("briefingsView.campaignFormat", "Campaign Format")}
                    value={formState.campaignFormat || "ALL"}
                    onChange={(event) =>
                      handleChange("campaignFormat", event.target.value)
                    }
                  >
                    {["VIDEO", "DISPLAY", "NATIVE", "ALL"].map((fmt) => (
                      <MenuItem key={fmt} value={fmt}>
                        {t(`briefingsView.campaignFormatOptions.${fmt}`, fmt)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {formState.platform === "NATADS" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.nativeAdProvider}>
                    <InputLabel id="native-ad-provider-label">
                      {t(
                        "briefingsView.nativeAdProvider",
                        "Native Ad Provider"
                      )}
                    </InputLabel>
                    <Select
                      labelId="native-ad-provider-label"
                      label={t(
                        "briefingsView.nativeAdProvider",
                        "Native Ad Provider"
                      )}
                      value={formState.nativeAdProvider || ""}
                      onChange={(event) =>
                        handleChange("nativeAdProvider", event.target.value)
                      }
                      required
                    >
                      {["TABOOLA", "OUTBRAIN"].map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          {provider}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.nativeAdProvider && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, px: 1.5 }}
                      >
                        {errors.nativeAdProvider}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="campaign-type-label">
                    {t("briefingsView.campaignType", "Campaign Type")}
                  </InputLabel>
                  <Select
                    labelId="campaign-type-label"
                    label={t("briefingsView.campaignType", "Campaign Type")}
                    value={formState.briefingCampaignType || ""}
                    onChange={(event) =>
                      handleChange(
                        "briefingCampaignType",
                        event.target.value || null
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>{t("common.none", "None")}</em>
                    </MenuItem>
                    {getCampaignTypesForPlatform(formState.platform).map(
                      (type) => (
                        <MenuItem key={type} value={type}>
                          {t(
                            `briefingsCreate.form.briefingCampaignTypeOptions.${type}`,
                            type
                          )}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {canManageBriefings && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="investment-payment-label">
                      {t(
                        "briefingsView.investmentPayment",
                        "Investment Payment"
                      )}
                    </InputLabel>
                    <Select
                      labelId="investment-payment-label"
                      label={t(
                        "briefingsView.investmentPayment",
                        "Investment Payment"
                      )}
                      value={formState.investmentPayment || ""}
                      onChange={(event) =>
                        handleChange(
                          "investmentPayment",
                          event.target.value || null
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>{t("common.none", "None")}</em>
                      </MenuItem>
                      <MenuItem value="CLIENTE">
                        {t("briefingsView.paymentClient", "Client")}
                      </MenuItem>
                      <MenuItem value="TI_MEDIA_GROUP_SL">
                        {t(
                          "briefingsView.paymentTiMediaGroupSl",
                          "TI MEDIA GROUP SL"
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="text" onClick={handleBack}>
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("common.save", "Save")
                )}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default EditAssociatedCampaign;
